/**
 * Bump when tour content changes so completed users see the new flow once.
 * Title/body/primary text live in shared/locales/{he,en,ar,ru}/tour.json under
 * `steps.<id>.*`, looked up by step id in TourSheet.jsx / TourSpotlight.jsx.
 */
export const TOUR_VERSION = 3;

export const PRE_GROUP_STEPS = [
    {
        type: 'sheet',
        id: 'welcome',
        pose: 'wave',
    },
    {
        type: 'spotlight',
        id: 'home-welcome',
        target: 'home-welcome',
        route: '/',
        pose: 'point',
    },
    {
        type: 'spotlight',
        id: 'group-pill',
        target: 'group-pill',
        route: '/',
        pose: 'point',
    },
];

// The invite step used to live here — moved to PAGE_TOURS['/group'], where the
// invite card actually lives, instead of stopping over on /group mid-tour.
export const FULL_STEPS = [
    {
        type: 'sheet',
        id: 'full-intro',
        pose: 'wave',
    },
    {
        type: 'spotlight',
        id: 'balance',
        target: 'balance-hero',
        route: '/',
        pose: 'coins',
    },
    {
        type: 'spotlight',
        id: 'debts',
        target: 'debts-section',
        route: '/',
        pose: 'coins',
    },
    {
        type: 'spotlight',
        id: 'recent-expenses',
        target: 'recent-expenses',
        route: '/',
        pose: 'point',
    },
    {
        type: 'spotlight',
        id: 'main-nav',
        target: 'main-nav',
        route: '/',
        pose: 'point',
    },
    {
        type: 'spotlight',
        id: 'games-entry',
        target: 'games-entry',
        route: '/',
        pose: 'celebrate',
    },
    {
        type: 'spotlight',
        id: 'expense-fab',
        target: 'expense-fab',
        route: '/expenses',
        pose: 'point',
    },
];

// One short tour per page, run automatically the first time a signed-in user
// opens that page (see tour.context.jsx). Each id here is what gets recorded
// in User.tour.seen — a flat list rather than a boolean per page, so a new
// page tour never needs a schema change (see backend/models/User.js).
//
// `requireApartment`: only shown for apartment-type groups (the WhatsApp
// section doesn't exist for GENERAL groups).
// `skipOnDesktop`: only shown on the mobile layout — the desktop route for
// that page doesn't render the target element at all.
export const PAGE_TOURS = {
    '/group': {
        id: 'group-v2',
        steps: [
            {
                type: 'spotlight',
                id: 'group-members',
                target: 'group-members',
                route: '/group',
                pose: 'point',
            },
            {
                type: 'spotlight',
                id: 'group-invite',
                target: 'invite-member',
                route: '/group',
                pose: 'point',
            },
            {
                type: 'spotlight',
                id: 'group-recurring',
                target: 'recurring-expenses',
                route: '/group',
                pose: 'coins',
            },
            {
                type: 'spotlight',
                id: 'group-whatsapp',
                target: 'whatsapp-connect',
                route: '/group',
                requireApartment: true,
                pose: 'point',
            },
        ],
    },
    '/games': {
        id: 'games-v2',
        steps: [
            {
                type: 'spotlight',
                id: 'games-streak',
                target: 'streak-bar',
                route: '/games',
                pose: 'celebrate',
            },
            {
                type: 'spotlight',
                id: 'games-daily',
                target: 'daily-game',
                route: '/games',
                pose: 'point',
            },
            {
                type: 'spotlight',
                id: 'games-pick',
                target: 'game-card',
                route: '/games',
                pose: 'point',
            },
        ],
    },
    '/tasks': {
        id: 'tasks-v2',
        steps: [
            {
                type: 'spotlight',
                id: 'tasks-shopping-toggle',
                target: 'tasks-shopping-nav',
                route: '/tasks',
                skipOnDesktop: true,
                pose: 'point',
            },
            {
                type: 'spotlight',
                id: 'tasks-add',
                target: 'tasks-add',
                route: '/tasks',
                pose: 'point',
            },
            {
                type: 'spotlight',
                id: 'tasks-list',
                target: 'tasks-list',
                route: '/tasks',
                pose: 'point',
            },
        ],
    },
    '/shopping': {
        id: 'shopping',
        steps: [
            {
                type: 'spotlight',
                id: 'shopping-add',
                target: 'shopping-add',
                route: '/shopping',
                pose: 'point',
            },
            {
                type: 'spotlight',
                id: 'shopping-store',
                target: 'shopping-store',
                route: '/shopping',
                pose: 'point',
            },
            {
                type: 'spotlight',
                id: 'shopping-list',
                target: 'shopping-list',
                route: '/shopping',
                pose: 'point',
            },
        ],
    },
};

/** Resolve a page's tour to the steps that actually apply right now. */
export function resolvePageTourSteps(pathname, { isApartment, isDesktop }) {
    const entry = PAGE_TOURS[pathname];
    if (!entry) return null;
    const steps = entry.steps.filter((s) => {
        if (s.requireApartment && !isApartment) return false;
        if (s.skipOnDesktop && isDesktop) return false;
        return true;
    });
    return steps.length ? { id: entry.id, steps } : null;
}
